import { useState } from "react";
import { Apple, Copy, Check, Download, Cpu } from "lucide-react";

// Single source of truth for how a Mac gets (or regains) the MIDTC agent.
//
// This lives in one component because the same three artefacts are offered on both
// the Device Inventory page and Device 360, and they MUST NOT drift apart: the
// one-liner is the only supported install path, and it is also the recovery tool for
// a Mac whose agent has died. Every URL below is a stable endpoint that resolves
// server-side to the newest staged build (findInstaller sorts by mtime, and the
// installer script has its key + per-arch SHA injected at request time), so nothing
// here pins a version number that could go stale in the UI.
const API_BASE = process.env.REACT_APP_API_URL || "";
const BASE     = (API_BASE || "").replace(/\/api\/?$/, "");

export const MAC_INSTALL_CMD =
  `curl -fsSL ${BASE || "https://ticketing.midtc.com"}/downloads/mac-agent-install | sudo bash`;

// Colours are passed in rather than imported: the inventory page is on the shared
// design tokens while Device 360 carries its own deep-navy palette, and this block
// has to look native in both.
const FALLBACK = {
  panel: "rgba(255,255,255,0.03)", border: "rgba(148,163,184,0.22)",
  text: "#e6edf7", muted: "#93a6c9", faint: "#6f86ab", accent: "#4f6fff",
};

export default function MacAgentInstall({
  palette   = FALLBACK,
  compact   = false,   // device page: command + package links only, no heading
  heading   = "Install or repair the Mac agent",
  note      = null,     // extra line of context (e.g. "this Mac has no RustDesk ID")
}) {
  const P = { ...FALLBACK, ...palette };
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(MAC_INSTALL_CMD);
    } catch {
      // Clipboard API can be blocked; fall back to a temporary selection copy so
      // the tech still gets the command rather than an inert button.
      const ta = document.createElement("textarea");
      ta.value = MAC_INSTALL_CMD;
      ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } catch { /* nothing more we can do */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const pkgLink = (href, label, sub, Icon) => (
    <a
      href={`${BASE}${href}`}
      download
      style={{
        display: "flex", alignItems: "center", gap: 9, padding: "8px 11px",
        borderRadius: 8, textDecoration: "none", flex: "1 1 190px", minWidth: 0,
        background: P.panel, border: `1px solid ${P.border}`,
      }}
    >
      <Icon size={15} color={P.accent} style={{ flexShrink: 0 }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: P.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</div>
        <div style={{ fontSize: 10.5, color: P.faint }}>{sub}</div>
      </div>
      <Download size={13} color={P.faint} style={{ marginLeft: "auto", flexShrink: 0 }} />
    </a>
  );

  return (
    <div style={{
      background: P.panel, border: `1px solid ${P.border}`, borderRadius: 10,
      padding: compact ? "12px 14px" : "14px 16px",
    }}>
      {!compact && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <Apple size={15} color={P.text} />
          <span style={{ fontSize: 13, fontWeight: 700, color: P.text }}>{heading}</span>
        </div>
      )}
      <div style={{ fontSize: 11.5, color: P.muted, marginBottom: 9, lineHeight: 1.5 }}>
        {note || (
          <>Run this on the Mac. It installs the signed agent, registers the device, and RustDesk
          follows on the first check-in. Safe to re-run — it also repairs a Mac whose agent has died.</>
        )}
      </div>

      {/* The command itself — the supported path, so it leads. */}
      <div style={{ display: "flex", gap: 8, alignItems: "stretch", marginBottom: 10, flexWrap: "wrap" }}>
        <code style={{
          flex: "1 1 320px", minWidth: 0, padding: "9px 11px", borderRadius: 8,
          background: "rgba(0,0,0,0.28)", border: `1px solid ${P.border}`,
          color: P.text, fontSize: 11.5, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          overflowX: "auto", whiteSpace: "nowrap",
        }}>{MAC_INSTALL_CMD}</code>
        <button
          onClick={copy}
          style={{
            display: "flex", alignItems: "center", gap: 6, padding: "0 13px",
            borderRadius: 8, cursor: "pointer", flexShrink: 0,
            background: copied ? "rgba(34,197,94,0.15)" : "rgba(99,102,241,0.14)",
            border: `1px solid ${copied ? "rgba(34,197,94,0.4)" : "rgba(99,102,241,0.35)"}`,
            color: copied ? "#22c55e" : P.accent,
            fontSize: 11.5, fontWeight: 700, fontFamily: "inherit",
          }}
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>

      {/* Signed app packages, for a user-driven install rather than a tech at a shell. */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {pkgLink("/downloads/mac-arm64", "MIDTC for Mac", "Apple Silicon · signed .zip", Apple)}
        {pkgLink("/downloads/mac",       "MIDTC for Mac", "Intel x64 · signed .zip",     Apple)}
        {!compact && pkgLink("/downloads/rmm-mac", "RMM agent binaries", "Both arches · signed, self-contained", Cpu)}
      </div>

      {!compact && (
        <div style={{ fontSize: 10.5, color: P.faint, marginTop: 9, lineHeight: 1.5 }}>
          Gatekeeper warns on first launch until Apple notarization is re-enabled — the packages are
          Developer ID signed. Grant RustDesk Screen Recording + Accessibility once per Mac for
          unattended control.
        </div>
      )}
    </div>
  );
}
